import { useState, useMemo } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { BudgetRevisionTimeline } from './budget-revision-timeline';
import { RevisionAuditCard } from './revision-audit-card';
import { RevisionCompareSelector } from './revision-compare-selector';
import { RevisionCompareView } from './revision-compare-view';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import type { BudgetRevision } from '@/types/budget.types';
import {
  History,
  Info,
  FileText,
  Search,
  X,
  ArrowLeft,
  ArrowLeftRight,
  Plus,
  Copy,
} from 'lucide-react';

type BudgetLayer = 'resource' | 'cost' | 'sales';

interface RevisionDetailsViewProps {
  revisions: BudgetRevision[];
  projectName: string;
  /** Controls version prefix, sheet title copy, and filter behaviour. Defaults to 'resource'. */
  budgetLayer?: BudgetLayer;
  onCompare?: (rev1: string, rev2: string) => void;
  onRestore?: (revisionId: string) => void;
  /** Called when PM clicks "View This Revision" — opens Edit Budget pre-loaded with snapshot */
  onViewRevision?: (revisionId: number | string) => void;
  /** Called when PM clicks "Make Active" on a draft revision */
  onMakeActive?: (revisionId: number | string) => void;
  /** Called when PM clicks "+ New Revision" — opens a blank Edit Budget screen */
  onNewRevision?: () => void;
  /** Called when PM clicks "Duplicate" on a revision card — opens Edit screen pre-loaded with that revision's data */
  onDuplicate?: (revisionId: number | string) => void;
}

// ─── Sort helpers (removed — revisions are shown in natural order) ────────────

function sortRevisions(revisions: BudgetRevision[]): BudgetRevision[] {
  return [...revisions];
}

// ─── Main component ───────────────────────────────────────────────────────────

// ─── Layer helpers ────────────────────────────────────────────────────────────

function layerTitle(layer: BudgetLayer): string {
  if (layer === 'cost') return 'Cost Budget Revision History';
  if (layer === 'sales') return 'Sales Budget Revision History';
  return 'Budget Revision History';
}

export function RevisionDetailsView({
  revisions,
  projectName,
  budgetLayer = 'resource',
  onCompare,
  onRestore,
  onViewRevision,
  onMakeActive,
  onNewRevision,
  onDuplicate,
}: RevisionDetailsViewProps) {
  // ── Filters & sort ──
  const [searchAuthor, setSearchAuthor] = useState('');
  const [filterCategory, setFilterCategory] = useState<string>('all');
  const [filterDateFrom, setFilterDateFrom] = useState('');
  const [filterDateTo, setFilterDateTo] = useState('');

  // ── Compare mode ──
  const [compareMode, setCompareMode] = useState(false);
  const [compareRevA, setCompareRevA] = useState<BudgetRevision | null>(null);
  const [compareRevB, setCompareRevB] = useState<BudgetRevision | null>(null);
  const [compareActive, setCompareActive] = useState(false);


  // Derive unique category options from the revision list.
  // For cost/sales layers, include 'resource-budget-cascade' when present (system-generated cascade).
  const categoryOptions = useMemo(() => {
    const seen = new Set<string>();
    revisions.forEach((r) => {
      if (r.reasonCategory) seen.add(r.reasonCategory);
      if (r.reasonCategoryLabel) seen.add(r.reasonCategory ?? r.reasonCategoryLabel);
    });
    return Array.from(seen);
  }, [revisions]);

  // Derive unique authors
  const authorOptions = useMemo(() => {
    const seen = new Set<string>();
    revisions.forEach((r) => {
      const name = r.createdBy ?? r.changedByName;
      if (name) seen.add(name);
    });
    return Array.from(seen);
  }, [revisions]);

  const filtered = useMemo(() => {
    let result = revisions;

    if (filterCategory && filterCategory !== 'all') {
      result = result.filter(
        (r) => r.reasonCategory === filterCategory || r.reasonCategoryLabel === filterCategory
      );
    }

    if (searchAuthor) {
      const q = searchAuthor.toLowerCase();
      result = result.filter(
        (r) =>
          (r.createdBy ?? r.changedByName ?? '').toLowerCase().includes(q)
      );
    }

    if (filterDateFrom) {
      const from = new Date(filterDateFrom).getTime();
      result = result.filter(
        (r) => new Date(r.createdAt ?? r.date).getTime() >= from
      );
    }

    if (filterDateTo) {
      const to = new Date(filterDateTo).getTime();
      result = result.filter(
        (r) => new Date(r.createdAt ?? r.date).getTime() <= to
      );
    }

    return sortRevisions(result);
  }, [revisions, filterCategory, searchAuthor, filterDateFrom, filterDateTo]);

  const hasActiveFilters =
    filterCategory !== 'all' ||
    searchAuthor !== '' ||
    filterDateFrom !== '' ||
    filterDateTo !== '';

  const clearFilters = () => {
    setFilterCategory('all');
    setSearchAuthor('');
    setFilterDateFrom('');
    setFilterDateTo('');
  };

  const handleView = (revision: BudgetRevision) => {
    onViewRevision?.(revision.id ?? revision.revisionId ?? '');
  };

  const handleCompare = (revision: BudgetRevision) => {
    // Pre-populate Rev A with the clicked revision, Rev B defaults to current
    const currentRevision = revisions.find((r) => r.status === 'current' || r.status === 'active') ?? revisions[0];
    setCompareRevA(revision);
    setCompareRevB(currentRevision ?? null);
    setCompareActive(false);
    setCompareMode(true);
  };

  const handleRunCompare = (revA: BudgetRevision, revB: BudgetRevision) => {
    setCompareRevA(revA);
    setCompareRevB(revB);
    setCompareActive(true);
  };

  const handleExitCompare = () => {
    setCompareMode(false);
    setCompareActive(false);
    setCompareRevA(null);
    setCompareRevB(null);
  };

  const handleRestore = (revision: BudgetRevision) => {
    onRestore?.(String(revision.revisionId ?? revision.id));
  };

  const handleDuplicate = (revision: BudgetRevision) => {
    onDuplicate?.(revision.id ?? revision.revisionId ?? '');
  };

  // ── Compare mode ──
  if (compareMode) {
    return (
      <div className="space-y-4">
        {/* Back button */}
        <Button variant="ghost" size="sm" onClick={handleExitCompare} className="h-8 px-2 text-sm">
          <ArrowLeft className="h-4 w-4 mr-1.5" />
          Back to Revision List
        </Button>

        {/* Selector */}
        <RevisionCompareSelector
          revisions={revisions}
          budgetLayer={budgetLayer}
          onCompare={handleRunCompare}
          initialRevAId={String(compareRevA?.revisionId ?? compareRevA?.id ?? '')}
          initialRevBId={String(compareRevB?.revisionId ?? compareRevB?.id ?? '')}
        />

        {/* Results */}
        {compareActive && compareRevA && compareRevB && (
          <RevisionCompareView
            revisionA={compareRevA}
            revisionB={compareRevB}
            budgetLayer={budgetLayer}
            onSwap={() => {
              setCompareRevA(compareRevB);
              setCompareRevB(compareRevA);
            }}
            onViewRevA={() => {
              handleExitCompare();
              onViewRevision?.(compareRevA.id ?? compareRevA.revisionId ?? '');
            }}
            onViewRevB={() => {
              handleExitCompare();
              onViewRevision?.(compareRevB.id ?? compareRevB.revisionId ?? '');
            }}
          />
        )}
      </div>
    );
  }

  // ── Empty state ──
  if (!revisions || revisions.length === 0) {
    return (
      <Card>
        <CardContent className="pt-6">
          <div className="flex flex-col items-center justify-center py-12 text-center">
            <History className="h-12 w-12 text-muted-foreground mb-4" />
            <h3 className="text-lg font-semibold mb-2">No Revision History</h3>
            <p className="text-muted-foreground max-w-md">
              Budget revisions will appear here once changes are made to the{' '}
              {budgetLayer === 'cost' ? 'cost' : budgetLayer === 'sales' ? 'sales' : 'resource'} budget.
              Each revision tracks who changed what, when, and why.
            </p>
          </div>
        </CardContent>
      </Card>
    );
  }

  const currentRevision = revisions.find((r) => r.status === 'current') ?? revisions[0];

  return (
    <div className="space-y-6">
      {/* ── Summary strip ── */}
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <CardTitle className="flex items-center gap-2">
              <Info className="h-5 w-5" />
              {layerTitle(budgetLayer)} — {projectName}
            </CardTitle>
            {onNewRevision && (
              <Button size="sm" onClick={onNewRevision}>
                <Plus className="h-4 w-4 mr-1.5" />
                New Revision
              </Button>
            )}
          </div>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="space-y-1">
              <div className="text-sm text-muted-foreground">Current Revision</div>
              <div className="text-xl font-semibold">{currentRevision?.revisionNumber ?? 'N/A'}</div>
            </div>
            <div className="space-y-1">
              <div className="text-sm text-muted-foreground">Total Revisions</div>
              <div className="text-xl font-semibold">{revisions.length}</div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* ── Tabs ── */}
      <Tabs defaultValue="list" className="w-full">
        <TabsList>
          <TabsTrigger value="list">
            <FileText className="h-4 w-4 mr-2" />
            Revision List
          </TabsTrigger>
          <TabsTrigger value="timeline">
            <History className="h-4 w-4 mr-2" />
            Timeline
          </TabsTrigger>
        </TabsList>

        {/* ── List tab ── */}
        <TabsContent value="list" className="space-y-4 mt-4">
          {/* Filter / sort bar */}
          <div className="flex flex-wrap items-center gap-3">
            {/* Author search */}
            <div className="relative">
              <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground" />
              <Input
                placeholder="Filter by author…"
                value={searchAuthor}
                onChange={(e) => setSearchAuthor(e.target.value)}
                className="pl-8 h-9 w-48 text-sm"
              />
            </div>

            {/* Category filter */}
            <Select value={filterCategory} onValueChange={setFilterCategory}>
              <SelectTrigger className="h-9 w-48 text-sm">
                <SelectValue placeholder="All categories" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All categories</SelectItem>
                {categoryOptions.map((cat) => (
                  <SelectItem key={cat} value={cat}>
                    {cat.replace(/-/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase())}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>

            {/* Date range */}
            <Input
              type="date"
              value={filterDateFrom}
              onChange={(e) => setFilterDateFrom(e.target.value)}
              className="h-9 w-36 text-sm"
              placeholder="From"
              title="From date"
            />
            <Input
              type="date"
              value={filterDateTo}
              onChange={(e) => setFilterDateTo(e.target.value)}
              className="h-9 w-36 text-sm"
              placeholder="To"
              title="To date"
            />

            {/* Clear filters */}
            {hasActiveFilters && (
              <Button
                variant="ghost"
                size="sm"
                onClick={clearFilters}
                className="h-9 text-muted-foreground"
              >
                <X className="h-4 w-4 mr-1" />
                Clear
              </Button>
            )}
          </div>

          {/* Result count */}
          {hasActiveFilters && (
            <p className="text-sm text-muted-foreground">
              Showing {filtered.length} of {revisions.length} revisions
              {filtered.length === 0 && ' — no revisions match your filters'}
            </p>
          )}

          {/* Active filters summary */}
          {hasActiveFilters && (
            <div className="flex flex-wrap gap-2">
              {filterCategory !== 'all' && (
                <Badge variant="secondary" className="gap-1">
                  Category:{' '}
                  {filterCategory
                    .replace(/-/g, ' ')
                    .replace(/\b\w/g, (c) => c.toUpperCase())}
                  <button onClick={() => setFilterCategory('all')}>
                    <X className="h-3 w-3" />
                  </button>
                </Badge>
              )}
              {searchAuthor && (
                <Badge variant="secondary" className="gap-1">
                  Author: {searchAuthor}
                  <button onClick={() => setSearchAuthor('')}>
                    <X className="h-3 w-3" />
                  </button>
                </Badge>
              )}
              {filterDateFrom && (
                <Badge variant="secondary" className="gap-1">
                  From: {filterDateFrom}
                  <button onClick={() => setFilterDateFrom('')}>
                    <X className="h-3 w-3" />
                  </button>
                </Badge>
              )}
              {filterDateTo && (
                <Badge variant="secondary" className="gap-1">
                  To: {filterDateTo}
                  <button onClick={() => setFilterDateTo('')}>
                    <X className="h-3 w-3" />
                  </button>
                </Badge>
              )}
            </div>
          )}

          {/* Audit cards */}
          <div className="space-y-4">
            {filtered.map((revision) => (
              <RevisionAuditCard
                key={revision.revisionId ?? revision.id}
                revision={revision}
                budgetLayer={budgetLayer}
                isCurrent={revision.status === 'current' || revision.status === 'active'}
                onView={() => handleView(revision)}
                onCompare={() => handleCompare(revision)}
                onRestore={() => handleRestore(revision)}
                onMakeActive={onMakeActive ? () => onMakeActive(revision.id ?? '') : undefined}
                onDuplicate={onDuplicate ? () => handleDuplicate(revision) : undefined}
              />
            ))}
            {filtered.length === 0 && (
              <div className="text-center text-muted-foreground py-8">
                No revisions match your filters.
              </div>
            )}
          </div>

          {/* Footer */}
          {revisions.length > 1 && (
            <div className="flex items-center justify-between p-4 bg-muted rounded-lg text-sm text-muted-foreground">
              <span>
                <strong>{revisions.length}</strong> total revisions
              </span>
              <span>
                Current: <strong>{revisions[0]?.revisionNumber}</strong>
              </span>
            </div>
          )}
        </TabsContent>

        {/* ── Timeline tab ── */}
        <TabsContent value="timeline">
          <BudgetRevisionTimeline revisions={revisions} />
        </TabsContent>
      </Tabs>
    </div>
  );
}
